import { useEffect } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import {
  getGetProductsByDisplayCollectionIdV3QueryKey,
  getProductsByDisplayCollectionIdV3,
} from '@/apis/rest/generated/hooks'
import { PAGE_LIMIT } from '@/constants/pages.const'
import { useCustomRouter } from '@/containers/hooks'
import { getSortDirectionByOrder, parseSortByOptionSelected } from '@/utils/getSortDirectionByOrder'
import { decodeId } from '@/utils/utilApi'

type UseDisplayCollectionProductListHook = {
  displayCollectionId: string
  exposeHiddenProduct?: boolean
}

const useDisplayCollectionProductListV3 = ({
  displayCollectionId,
  exposeHiddenProduct = false,
}: UseDisplayCollectionProductListHook) => {
  const { query } = useCustomRouter()
  const { ref, inView } = useInView()

  const orderBy = (query?.orderBy as string) || 'SALES_SCORE'

  const {
    data: displayCollectionData,
    hasNextPage,
    fetchNextPage,
    isLoading,
  } = useInfiniteQuery(
    getGetProductsByDisplayCollectionIdV3QueryKey(Number(decodeId(displayCollectionId).id), {
      sortBy: parseSortByOptionSelected(orderBy) || 'SALES_SCORE',
      sortDirection: getSortDirectionByOrder(orderBy),
    }),
    ({ pageParam = PAGE_LIMIT.DEFAULT_PAGE }) =>
      getProductsByDisplayCollectionIdV3(Number(decodeId(displayCollectionId).id), {
        sortBy: parseSortByOptionSelected(orderBy) || 'SALES_SCORE',
        sortDirection: getSortDirectionByOrder(orderBy),
        page: pageParam,
        size: PAGE_LIMIT.LIST,
        sortSoldOutToLast: true,
        exposeHiddenProduct,
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.data?.products?.length === PAGE_LIMIT.LIST
          ? allPages.length + PAGE_LIMIT.DEFAULT_PAGE
          : undefined
      },
    }
  )

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  const totalCount = displayCollectionData?.pages[0]?.page?.totalCount || 0
  const title = displayCollectionData?.pages[0].data?.name || ''
  const type = displayCollectionData?.pages[0].data?.collectionType || ''
  return {
    title,
    type,
    ref,
    isLoading,
    displayCollectionData,
    orderBy,
    totalCount,
  }
}

export default useDisplayCollectionProductListV3
